// ════════════════════════════════════════════════════════════════
// FloorCost — Supabase Auth (REST)
// ────────────────────────────────────────────────────────────────
// Talks to the Supabase GoTrue endpoints directly via fetch —
// mirrors src/features/sync/supabase.js's raw-REST style so we don't pull in
// @supabase/supabase-js (would be a ~20 kB gzipped dep for a
// single-pilot user).
//
// Magic-link only in this PR. Passkey (WebAuthn) support lands in
// a focused follow-up so the registration ceremony + Supabase MFA
// config get their own diff.
//
// Session storage convention: a single JSON blob under STORAGE.auth.
// Reading it is the source-of-truth for "am I signed in?" — there's
// no network call to ask Supabase. Refresh tokens kick in only when
// the access token is past expiry.
// ════════════════════════════════════════════════════════════════

import { SB_URL, SB_KEY, SB_ENABLED, STORAGE } from '#lib/config.js';
import { lsSet } from '#features/sync/storage.js';

const AUTH_KEY = STORAGE.auth;
const AUTH_PENDING_KEY = STORAGE.authPending;

// How long a requested sign-in stays consumable. Matches GoTrue's
// default magic-link/OTP validity (1 hour) — a link older than this
// is dead server-side anyway, so the marker has nothing to authorise.
const AUTH_PENDING_TTL_MS = 60 * 60 * 1000;

/**
 * @typedef {Object} AuthSession
 * @property {string} access_token   JWT signed by Supabase; carries auth.uid().
 * @property {string} refresh_token  Long-lived token used to mint a new access_token.
 * @property {number} expires_at     Unix seconds at which access_token stops being valid.
 * @property {{ id: string, email: string }} user
 */

// ── Storage ──────────────────────────────────────────────────────

/** @returns {AuthSession | null} */
export function getSession() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== 'object' || !s.access_token) return null;
    return s;
  } catch {
    return null;
  }
}

/**
 * Persist a session (or clear it) and notify subscribers. Exported
 * so the passkey flow can install a freshly-refreshed session
 * minted from the wrapped refresh_token without going through a
 * magic-link round-trip.
 *
 * Side effect: (re)schedules the background refresh timer to fire
 * 60 s before the new token's `expires_at`, so the operator never
 * sees a 401-induced sign-out as long as the tab stays open. The
 * timer self-perpetuates on each successful refresh.
 *
 * @param {AuthSession | null} s
 */
export function setSession(s) {
  if (!s) {
    // Deliberately does NOT clear a stashed callback error: the
    // boot-time refresh of a long-dead session 4xxes into
    // setSession(null) within a second of the expired-link message
    // being stashed, and would race it off the surface before the
    // operator could read it. Signed out is exactly the state the
    // message exists for.
    try { localStorage.removeItem(AUTH_KEY); } catch { /* private mode */ }
    _clearRefreshTimer();
    _notify(null);
    return;
  }
  // A live session makes any stashed callback error moot — and likewise the
  // previous sign-out's "not revoked" caveat: the operator is signed in again.
  _callbackError = '';
  _signOutNotice = null;
  lsSet(AUTH_KEY, JSON.stringify(s));
  _scheduleRefresh(s);
  _notify(s);
}

/**
 * Pull the current user's Supabase auth uid out of the stored
 * session. Returns null when there's no session (e.g. signed out,
 * or a transient gap during boot) so callers can pass it straight
 * into a nullable `user_id` column.
 *
 * Used at the persistence boundary in src/features/sync/supabase.js to stamp
 * every write with per-user attribution — feeds Tier B-2's RLS
 * policy switch (`auth.uid() = user_id`) once that lands.
 *
 * @returns {string | null}
 */
export function getCurrentUserId() {
  const s = getSession();
  return (s && s.user && s.user.id) ? s.user.id : null;
}

/**
 * The current session's access_token, or null when signed out. Used to
 * authenticate calls to the operator-only Netlify Functions (S1), which
 * verify it server-side via GoTrue.
 * @returns {string | null}
 */
export function getAccessToken() {
  const s = getSession();
  return (s && s.access_token) ? s.access_token : null;
}

/** @returns {boolean} */
export function isSignedIn() {
  const s = getSession();
  if (!s) return false;
  // Treat sessions with <60s left as already expired so callers
  // don't fire a request the token can't satisfy.
  return s.expires_at * 1000 > Date.now() + 60_000;
}

// ── Pending sign-in marker (SEC-A2) ──────────────────────────────
// consumeCallbackHash() honours URL-fragment tokens only when this
// browser actually asked for a sign-in. Without the binding, a crafted
// /app#access_token=… link silently signs the operator into whatever
// account the tokens belong to (login-CSRF / session fixation). The
// marker is written when a magic link is requested and cleared once a
// sign-in completes, so an unsolicited token-bearing link finds no
// pending sign-in and is refused.

function _markSignInPending() {
  try { localStorage.setItem(AUTH_PENDING_KEY, String(Date.now())); } catch { /* private mode */ }
}

/** @returns {boolean} */
function _hasPendingSignIn() {
  try {
    const raw = localStorage.getItem(AUTH_PENDING_KEY);
    if (!raw) return false;
    const t = Number(raw);
    return Number.isFinite(t) && Date.now() - t < AUTH_PENDING_TTL_MS;
  } catch {
    return false;
  }
}

function _clearPendingSignIn() {
  try { localStorage.removeItem(AUTH_PENDING_KEY); } catch { /* private mode */ }
}

// ── Callback error surface ───────────────────────────────────────
// GoTrue reports a failed magic link as an error fragment
// (`#error=access_denied&error_code=otp_expired&error_description=…`)
// instead of tokens — most commonly an expired or already-consumed
// link (they're single-use with a 1 h validity, and mail scanners
// sometimes pre-consume them). Without a surface for that fragment
// the operator lands back on a mute sign-in form with no hint the
// click failed. consumeCallbackHash() stashes a message here;
// renderSignIn() reads it and shows it in the form's error slot.

/** @type {string} */
let _callbackError = '';

/**
 * Operator-facing message from the most recent failed auth callback,
 * or '' when there isn't one. Not consumed on read — the boot router
 * can re-render the sign-in surface several times in one paint chain,
 * and the message must survive until it's obsolete: a fresh link is
 * requested, a session is installed, or the operator dismisses it
 * (clearAuthCallbackError).
 *
 * @returns {string}
 */
export function getAuthCallbackError() {
  return _callbackError;
}

/**
 * Drop the stashed callback error. Called by the sign-in surface when
 * the operator dismisses the message (starts typing), so a later
 * re-render doesn't resurrect it.
 *
 * @returns {void}
 */
export function clearAuthCallbackError() {
  _callbackError = '';
}

/**
 * Map a GoTrue error_code to operator-facing copy. The fragment is
 * attacker-craftable URL text, so unknown codes get generic copy
 * rather than being echoed into a trusted surface.
 *
 * @param {string} code
 * @returns {string}
 */
function _describeAuthError(code) {
  if (code === 'otp_expired') {
    return 'That sign-in link has expired or was already used — request a fresh one below.';
  }
  return 'Sign-in didn’t complete — request a fresh link below.';
}

// ── Listener registry ────────────────────────────────────────────

/** @type {Set<(s: AuthSession | null) => void>} */
const _listeners = new Set();

/**
 * Subscribe to session changes. Fires immediately with the current
 * session (mirrors Supabase's onAuthStateChange shape).
 * @param {(s: AuthSession | null) => void} fn
 * @returns {() => void} unsubscriber
 */
export function onAuthStateChange(fn) {
  _listeners.add(fn);
  try { fn(getSession()); } catch { /* listener threw — caller's problem */ }
  return () => _listeners.delete(fn);
}

/** @param {AuthSession | null} s */
function _notify(s) {
  for (const fn of _listeners) {
    try { fn(s); } catch { /* one bad listener can't break the rest */ }
  }
}

// ── Background refresh timer ─────────────────────────────────────
// PR-3: Supabase access tokens default to 1-hour lifetimes; without
// proactive refresh, every long-lived tab eventually hits a 401 and
// the operator gets bounced to sign-in mid-task. The timer fires
// 60 s before the stored `expires_at` and calls `refreshSession`,
// which rotates the access + refresh tokens, persists the new
// session, and re-schedules itself via setSession → _scheduleRefresh.
//
// Skipping the timer (and falling back to per-request 401 retry)
// would mean threading retry logic through 16+ fetch sites in
// supabase.js. The timer keeps the supabase boundary clean — by
// the time any request fires, the token is always fresh.

const REFRESH_SLACK_MS = 60_000;
// A network error or 5xx during the scheduled refresh does NOT mean the
// session is dead, so we keep it and retry a bounded number of times with
// a fixed backoff before deferring to the per-request 401 re-auth path.
const REFRESH_RETRY_MS = 30_000;
const MAX_REFRESH_RETRIES = 6;
let _refreshRetries = 0;
/** @type {ReturnType<typeof setTimeout> | null} */
let _refreshTimer = null;

function _clearRefreshTimer() {
  if (_refreshTimer !== null) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

/** @param {AuthSession} s */
function _scheduleRefresh(s) {
  _clearRefreshTimer();
  if (typeof setTimeout !== 'function') return;
  _refreshRetries = 0; // fresh, valid session — reset the transient-retry budget
  const delay = Math.max(0, s.expires_at * 1000 - Date.now() - REFRESH_SLACK_MS);
  _refreshTimer = setTimeout(_runRefresh, delay);
}

/**
 * Fire a scheduled refresh.
 *
 * Reads the *current* refresh token from storage at fire time rather than
 * one captured when the timer was scheduled, so a token already rotated by
 * another tab isn't replayed stale.
 *
 * Failure handling distinguishes fatal from transient: a 4xx means the
 * refresh token itself was rejected (revoked / expired / already-rotated)
 * and the session is genuinely dead — clear it so the next paint routes to
 * sign-in. A network error or a 5xx is transient: the session may well
 * still be valid, so keep it and retry with a fixed backoff up to
 * MAX_REFRESH_RETRIES, after which the per-request 401 path re-auths if
 * needed. Destroying a valid session on a passing network blip — bouncing
 * the operator to sign-in mid-quote — was the bug this guards.
 */
function _runRefresh() {
  _refreshTimer = null;
  const cur = getSession();
  if (!cur || !cur.refresh_token) return; // signed out meanwhile — nothing to refresh
  refreshSession(cur.refresh_token).catch((err) => {
    const status = err && typeof err.status === 'number' ? err.status : 0;
    if (status >= 400 && status < 500) {
      setSession(null);
    } else if (_refreshRetries < MAX_REFRESH_RETRIES && typeof setTimeout === 'function') {
      _refreshRetries += 1;
      _refreshTimer = setTimeout(_runRefresh, REFRESH_RETRY_MS);
    }
    // else: transient retries exhausted — leave the session in place; the
    // next authenticated request's 401 path re-auths if the token expired.
  });
}

/**
 * Boot-time entry point. If a session already exists in storage
 * (e.g. the user reloaded the tab), schedule its refresh. Idempotent
 * and safe to call multiple times — `_scheduleRefresh` clears the
 * prior timer first.
 *
 * @returns {void}
 */
export function ensureRefreshTimer() {
  const s = getSession();
  if (s) _scheduleRefresh(s);
}

// ── HTTP ─────────────────────────────────────────────────────────

function authU(path) {
  return SB_URL + '/auth/v1/' + path;
}

function authH(extra) {
  return {
    apikey: SB_KEY,
    'Content-Type': 'application/json',
    ...(extra || {}),
  };
}

// ── Magic-link sign-in ───────────────────────────────────────────

/**
 * Email the user a sign-in link. They click it, GoTrue redirects to
 * `/auth/callback`, our callback handler stashes the session.
 *
 * Per Supabase GoTrue: POST /auth/v1/otp with `{ email, create_user }`
 * sends a one-time magic link (and a 6-digit OTP code as a fallback
 * for environments where deep links don't work).
 *
 * @param {string} email
 * @param {{ createUser?: boolean }} [opts]
 * @returns {Promise<void>}
 */
export async function signInWithMagicLink(email, opts = {}) {
  if (!SB_ENABLED) throw new Error('Auth unavailable — Supabase not configured');
  const trimmed = String(email || '').trim().toLowerCase();
  if (!trimmed) throw new Error('Email is required');

  const r = await fetch(authU('otp'), {
    method: 'POST',
    headers: authH(),
    body: JSON.stringify({
      email: trimmed,
      create_user: opts.createUser !== false,
      options: {
        // Where GoTrue redirects after the user clicks the link.
        // Must be on Supabase's redirect-allowlist (configured in
        // the dashboard once at project setup).
        email_redirect_to: window.location.origin + '/auth/callback',
      },
    }),
  });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`Magic-link request failed (${r.status}) ${body}`);
  }
  // SEC-A2: authorise the upcoming callback — only a browser that
  // reached this point may consume fragment tokens.
  _markSignInPending();
  // A fresh link is on its way — a stale dead-link message no longer
  // describes anything actionable.
  _callbackError = '';
}

// ── Magic-link callback ──────────────────────────────────────────

/**
 * Parse the URL hash GoTrue returned and persist the session.
 * Called by the router when the route is `/auth/callback`.
 *
 * GoTrue puts the tokens in the URL fragment as
 * `#access_token=...&refresh_token=...&expires_in=3600&type=magiclink`.
 *
 * SEC-A2 guards: tokens are honoured only when (a) no live session
 * exists — a signed-in operator is never silently re-signed into a
 * different account by a crafted link — and (b) this browser initiated
 * a sign-in within the last hour (the pending marker written by
 * signInWithMagicLink). Refused tokens are stripped from the URL so
 * they don't linger in history / Sentry breadcrumbs either way.
 *
 * @returns {AuthSession | null}
 */
export function consumeCallbackHash() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!hash) return null;
  const p = new URLSearchParams(hash);
  const access_token = p.get('access_token');
  const refresh_token = p.get('refresh_token');

  const stripHash = () => {
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch { /* non-browser env */ }
  };

  if (!access_token || !refresh_token) {
    // No tokens — either GoTrue reporting a failed link via an error
    // fragment (strip it so it doesn't linger in history / Sentry
    // breadcrumbs), or an unrelated hash (in-page anchor) that must
    // pass through untouched. The message is stashed only while
    // signed out: a signed-in operator never sees the sign-in surface,
    // and a stale stash could resurface hours later as a misleading
    // error after an unrelated sign-out. Only a charset-checked code
    // reaches the console — description text is fragment-sourced and
    // would flow into Sentry breadcrumbs verbatim.
    const errorCode = p.get('error_code') || p.get('error') || '';
    if (errorCode) {
      if (!isSignedIn()) _callbackError = _describeAuthError(errorCode);
      const safeCode = /^[\w.-]{1,64}$/.test(errorCode) ? errorCode : '(unrecognised)';
      console.warn('[auth] Sign-in callback returned an error:', safeCode);
      stripHash();
    }
    return null;
  }

  if (isSignedIn()) {
    console.warn('[auth] Ignoring URL-fragment tokens — already signed in (SEC-A2)');
    stripHash();
    return null;
  }
  if (!_hasPendingSignIn()) {
    console.warn('[auth] Ignoring URL-fragment tokens — no sign-in was requested from this browser (SEC-A2)');
    stripHash();
    return null;
  }

  const expires_in = parseInt(p.get('expires_in') || '3600', 10);
  const user = _decodeUserFromJwt(access_token);
  const session = {
    access_token,
    refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
    user,
  };
  _clearPendingSignIn();
  setSession(session);
  // Wipe the tokens out of the URL so they don't end up in browser
  // history, copy-paste, or Sentry breadcrumbs.
  stripHash();
  return session;
}

// ── OTP-code sign-in (PWA / cross-context path) ──────────────────

/**
 * Verify a 6-digit email code and persist the resulting session.
 *
 * Why this exists alongside the magic link: inside an iOS
 * home-screen PWA the link flow is broken. Mail hands the
 * `https://` link to Safari, whose storage is isolated from the
 * standalone web app, so `consumeCallbackHash()` sets the session
 * in Safari and the PWA never sees it. The same `/auth/v1/otp`
 * request that mails the link also mails a 6-digit code; verifying
 * that code here runs entirely in the PWA's own context, so the
 * session lands where the app can read it — no cross-app redirect.
 *
 * GoTrue: POST /auth/v1/verify with `{ type: 'email', email, token }`
 * returns a full session. `type: 'email'` is the unified email-OTP
 * verification type and covers the operator (an existing user)
 * regardless of whether the mail came from the magic-link or
 * signup-confirmation template.
 *
 * @param {string} email
 * @param {string} code  the 6-digit code from the email
 * @returns {Promise<AuthSession>}
 */
export async function verifyOtp(email, code) {
  if (!SB_ENABLED) throw new Error('Auth unavailable — Supabase not configured');
  const trimmedEmail = String(email || '').trim().toLowerCase();
  const token = String(code || '').replace(/\s+/g, '');
  if (!trimmedEmail) throw new Error('Email is required');
  if (!token) throw new Error('Code is required');

  const r = await fetch(authU('verify'), {
    method: 'POST',
    headers: authH(),
    body: JSON.stringify({ type: 'email', email: trimmedEmail, token }),
  });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`Code verification failed (${r.status}) ${body}`);
  }
  // A 2xx whose body is not JSON — a captive portal or proxy answering 200
  // with an HTML page — used to throw a raw SyntaxError out of here, past
  // every caller expecting this function's own error contract.
  let data;
  try {
    data = await r.json();
  } catch {
    throw new Error(`Code verification failed (${r.status}) unreadable response body`);
  }
  if (!data || typeof data !== 'object') {
    throw new Error('Verify response missing tokens');
  }
  const access_token = data.access_token;
  const refresh_token = data.refresh_token;
  const expires_in = parseInt(String(data.expires_in || 3600), 10);
  if (!access_token || !refresh_token) {
    throw new Error('Verify response missing tokens');
  }
  const user = _decodeUserFromJwt(access_token);
  const session = {
    access_token,
    refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
    user,
  };
  // Sign-in completed on this device — the emailed link no longer has
  // a pending sign-in to authorise (SEC-A2).
  _clearPendingSignIn();
  setSession(session);
  return session;
}

// ── Refresh-token grant ──────────────────────────────────────────

/**
 * Exchange a refresh_token for a fresh access_token + (rotated)
 * refresh_token. Supabase rotates refresh tokens on every use, so
 * callers MUST persist the *new* `refresh_token` returned in the
 * session — using the old one again will fail with 401.
 *
 * Used by the passkey sign-in flow: WebAuthn ceremony unlocks the
 * stored refresh_token, we trade it here for a usable session.
 *
 * @param {string} refreshToken
 * @returns {Promise<AuthSession>}
 */
export async function refreshSession(refreshToken) {
  if (!SB_ENABLED) throw new Error('Auth unavailable — Supabase not configured');
  if (!refreshToken) throw new Error('Refresh token required');

  const r = await fetch(authU('token?grant_type=refresh_token'), {
    method: 'POST',
    headers: authH(),
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    // Tag the HTTP status so the background-refresh timer can tell a
    // fatal auth rejection (4xx — refresh token revoked/expired) apart
    // from a transient server error (5xx) and avoid destroying a
    // still-valid session on the latter.
    const err = new Error(`Refresh failed (${r.status}) ${body}`);
    /** @type {{ status?: number }} */ (err).status = r.status;
    throw err;
  }
  // As in verifyOtp, but the status tag matters here: _runRefresh destroys the
  // session on 4xx and retries on anything else. A malformed 2xx body is not
  // an auth rejection — the refresh token is very likely still good — so it
  // carries r.status (2xx) and lands on the transient retry branch.
  let data;
  try {
    data = await r.json();
  } catch {
    const err = new Error(`Refresh failed (${r.status}) unreadable response body`);
    /** @type {{ status?: number }} */ (err).status = r.status;
    throw err;
  }
  if (!data || typeof data !== 'object') {
    const err = new Error(`Refresh failed (${r.status}) unexpected response shape`);
    /** @type {{ status?: number }} */ (err).status = r.status;
    throw err;
  }
  const access_token = data.access_token;
  const refresh_token = data.refresh_token;
  const expires_in = parseInt(String(data.expires_in || 3600), 10);
  if (!access_token || !refresh_token) {
    throw new Error('Refresh response missing tokens');
  }
  const user = _decodeUserFromJwt(access_token);
  const session = {
    access_token,
    refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
    user,
  };
  setSession(session);
  return session;
}

// ── Sign out ─────────────────────────────────────────────────────

/**
 * Clear the local session and, where possible, tell GoTrue to revoke the
 * refresh token. The local clear always happens first so the operator is never
 * stranded on a flaky connection — but that means "signed out" and "revoked"
 * are different outcomes, and this reports which one occurred:
 *
 *   - `revoked`              — local clear + GoTrue accepted the /logout.
 *   - `local-only-passkey`   — a passkey is registered, so /logout is skipped
 *                              deliberately (see below); the session remains
 *                              valid server-side.
 *   - `local-only-offline`   — /logout was attempted and failed (network down
 *                              or non-2xx); nothing was revoked.
 *   - `no-session`           — nothing to do.
 *
 * The result is also stashed for the sign-in surface to surface once, because
 * the caller hard-navigates immediately and a toast would not survive it.
 *
 * @returns {Promise<'revoked'|'local-only-passkey'|'local-only-offline'|'no-session'>}
 */
export async function signOut() {
  const s = getSession();
  /** @type {'revoked'|'local-only-passkey'|'local-only-offline'|'no-session'} */
  let outcome = 'revoked';
  // Peek at the passkey storage key directly rather than importing
  // hasPasskey() — auth.js is below passkey.js in the dependency
  // graph (passkey.js imports getSession/setSession/refreshSession
  // from here), so a back-import would cycle.
  const hasPasskey = (() => {
    try { return !!localStorage.getItem(STORAGE.passkey); } catch { return false; }
  })();
  setSession(null);
  if (!s || !SB_ENABLED) {
    _stashSignOutNotice('no-session');
    return 'no-session';
  }
  // Skip the server-side revoke when a passkey is registered.
  // Supabase's /logout invalidates the refresh_token, but that's
  // the very token the passkey wraps and replays on the next
  // "Sign in with passkey". Calling /logout here would render
  // every future passkey sign-in a 400 refresh_token_not_found.
  // The security boundary for a passkey-registered session is the
  // passkey itself (Face ID + iCloud Keychain + PRF-wrapped key),
  // not server-side revocation. If the operator wants a true
  // revocation, they remove the passkey first (which clears the
  // wrapped token from the device) and then sign out.
  if (hasPasskey) {
    _stashSignOutNotice('local-only-passkey');
    return 'local-only-passkey';
  }
  try {
    const r = await fetch(authU('logout'), {
      method: 'POST',
      headers: authH({ Authorization: 'Bearer ' + s.access_token }),
    });
    // A non-2xx is a failed revocation, not a completed one. Reporting it as
    // success is the specific thing E1-04 forbids: the refresh token is still
    // live server-side until it expires, and the operator should know.
    if (!r.ok) outcome = 'local-only-offline';
  } catch {
    // Network down — the local clear already happened, so the operator is not
    // stranded, but nothing was revoked.
    outcome = 'local-only-offline';
  }
  _stashSignOutNotice(outcome);
  return outcome;
}

/**
 * Persist the sign-out result for the sign-in surface to read once.
 *
 * Sign-out hard-navigates to `/`, which wipes module state — so the in-memory
 * `_callbackError` stash cannot carry this. A one-shot localStorage key can.
 *
 * @param {'revoked'|'local-only-passkey'|'local-only-offline'|'no-session'} outcome
 * @returns {void}
 */
function _stashSignOutNotice(outcome) {
  try { localStorage.setItem(STORAGE.signOutNotice, outcome); } catch { /* private mode */ }
  // Invalidate any value already lifted into memory, so this sign-out's result
  // is what gets read rather than a stale one. In production the hard
  // navigation makes this moot; it keeps the pair self-consistent anyway.
  _signOutNotice = undefined;
}

/** @type {string | null | undefined} undefined = not yet lifted from storage */
let _signOutNotice;

/**
 * The sign-out result stashed by the last `signOut()`, or null.
 *
 * One-shot per page load, NOT per call — the distinction matters. The boot
 * router re-renders the sign-in surface several times in one paint chain, so a
 * consume-on-read would be swallowed by a render whose output is then
 * discarded, and the operator would never see it. (Exactly what happened when
 * this was first written; the screenshot caught it, the unit tests did not,
 * because they called this directly instead of through a render.)
 *
 * The storage key is removed on the first read so a later reload can't
 * resurrect the message; the value stays in memory for the rest of this page's
 * paints, and is cleared when a session is installed.
 *
 * @returns {'revoked'|'local-only-passkey'|'local-only-offline'|'no-session'|null}
 */
export function getSignOutNotice() {
  if (_signOutNotice === undefined) {
    try {
      _signOutNotice = localStorage.getItem(STORAGE.signOutNotice);
      if (_signOutNotice) localStorage.removeItem(STORAGE.signOutNotice);
    } catch {
      _signOutNotice = null;
    }
  }
  return /** @type {any} */ (_signOutNotice) || null;
}


/**
 * Operator-facing copy for a sign-out result, or '' when there is nothing
 * worth saying. Only the outcomes that did NOT revoke server-side produce a
 * message — a clean revocation needs no explanation.
 *
 * @param {string | null} outcome
 * @returns {string}
 */
export function describeSignOutNotice(outcome) {
  if (outcome === 'local-only-passkey') {
    return 'Signed out on this device. Your passkey still works here — remove the passkey to revoke access everywhere.';
  }
  if (outcome === 'local-only-offline') {
    return 'Signed out on this device, but we couldn’t reach the server to revoke the session — it stays valid elsewhere until it expires.';
  }
  return '';
}

// ── JWT user extraction ──────────────────────────────────────────

/**
 * Decode the user `{ id, email }` from a Supabase access token JWT.
 * We don't verify the signature — Supabase signed it and we trust
 * the payload because we only ever store tokens we received from
 * GoTrue directly.
 *
 * Exported for tests.
 *
 * @param {string} jwt
 * @returns {{ id: string, email: string }}
 */
export function _decodeUserFromJwt(jwt) {
  try {
    const payload = jwt.split('.')[1];
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const json = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    return { id: json.sub || '', email: json.email || '' };
  } catch {
    return { id: '', email: '' };
  }
}

// ── Cross-tab session propagation (AUTH-2) ───────────────────────
// `getSession()` reads localStorage on every call, so the session DATA is
// already shared between tabs — but `_notify` only walks THIS tab's listener
// set. Signing out in one tab cleared the stored session while every other tab
// carried on showing the authenticated app: nothing gave boot.js's paint() a
// reason to re-run, so the operator was left looking at a signed-in surface
// whose next write went out unauthenticated and was refused by per-user RLS.
// On a counter where the browser is shared, that is the wrong default.
//
// `storage` fires in every OTHER same-origin tab when the key changes, so
// re-notifying from it lands both sign-out and sign-in everywhere. Derived from
// the stored session rather than a broadcast payload: the event only says the
// key changed, and `getSession()` remains the single reader of record, so the
// two cannot disagree. Same pattern as features/sync/queue.js and
// features/tracker/state.js.
//
// The timer is realigned, not just the UI. A tab that learns of a rotated
// session still holds a timer scheduled against the OLD `expires_at`, which by
// then has passed — it would fire immediately and force a redundant rotation.
// `_scheduleRefresh` clears the prior timer first, so re-arming here converges
// every tab on the stored session's real expiry.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (ev) => {
    // A null key is a wholesale clear (devtools, or another tab resetting);
    // that is a sign-out as far as this tab is concerned.
    if (ev.key !== null && ev.key !== AUTH_KEY) return;
    const s = getSession();
    if (s) _scheduleRefresh(s);
    else _clearRefreshTimer();
    _notify(s);
  });
}

// ── Bypass escape hatch (removed) ────────────────────────────────
// The `?bypass=<token>` escape hatch (AUTH_BYPASS_TOKEN) was removed in
// the S2 security pass: a client-embedded shared secret compiled into the
// public bundle, greppable from the deployed JS and — against permissive
// RLS — a full read/write bypass of the auth gate. Account recovery now
// relies solely on the three real auth paths (magic-link / OTP / passkey).
// See docs/SESSION_LOG.md 2026-06-11 and docs/BACKLOG.md §5 S2.
